export interface RnReclamationUserRef {
  _id: string;
  firstName?: string;
  lastName?: string;
  userName?: string;
  email?: string;
}

export type RnReclamationPriority = 'high' | 'medium' | 'low';
export type RnReclamationStatus = 'open' | 'answered' | 'closed';
export type RnReclamationMessageKind = 'request' | 'response';

export interface RnReclamationRecipient {
  userId: RnReclamationUserRef | string;
  username?: string;
}

export interface RnReclamationMessage {
  _id: string;
  author?: RnReclamationUserRef | string | null;
  authorName?: string;
  kind: RnReclamationMessageKind;
  text: string;
  createdAt: string;
}

export interface RnReclamationThread {
  _id: string;
  number?: string;
  rnWorkOrder: string;
  subject: string;
  priority: RnReclamationPriority;
  status: RnReclamationStatus;
  recipients: RnReclamationRecipient[];
  messages: RnReclamationMessage[];
  createdBy?: RnReclamationUserRef | string | null;
  createdAt: string;
  updatedAt: string;
  dateTo?: string | null;
}

export interface CreateRnReclamationThreadPayload {
  rnWorkOrder: string;
  subject: string;
  priority?: RnReclamationPriority;
  recipients: string[];
  text: string;
  dateTo?: string | null;
}

// POST /:id/messages body. Omitting `dateTo` = plain reply (deadline unchanged / "accept");
// sending it saves the agreed deadline on the thread.
export interface AddRnReclamationMessagePayload {
  text: string;
  dateTo?: string | null;
}

export interface RnReclamationThreadListResponse {
  data: RnReclamationThread[];
  page: number;
  totalPages: number;
  total: number;
  nextNumber?: string;
}

export const normalizeReclamationListResponse = (payload: unknown): RnReclamationThreadListResponse => {
  if (Array.isArray(payload)) {
    return { data: payload as RnReclamationThread[], page: 1, totalPages: 1, total: payload.length };
  }
  const envelope = (payload || {}) as Partial<RnReclamationThreadListResponse> & {
    rows?: RnReclamationThread[];
    pagination?: { page?: number; total?: number; pages?: number };
  };
  const data = Array.isArray(envelope.data) ? envelope.data : Array.isArray(envelope.rows) ? envelope.rows : [];
  const pagination = envelope.pagination;
  return {
    data,
    page: Number(pagination?.page ?? envelope.page) || 1,
    totalPages: Number(pagination?.pages ?? envelope.totalPages) || 1,
    total: Number(pagination?.total ?? envelope.total) || data.length,
    nextNumber: typeof envelope.nextNumber === 'string' ? envelope.nextNumber : '',
  };
};

export const normalizeReclamationResponse = (payload: unknown): RnReclamationThread | null => {
  if (!payload || typeof payload !== 'object') return null;
  const envelope = payload as { data?: unknown; _id?: unknown };
  if (envelope.data && typeof envelope.data === 'object' && typeof (envelope.data as { _id?: unknown })._id === 'string') {
    return envelope.data as RnReclamationThread;
  }
  if (typeof envelope._id === 'string') {
    return payload as RnReclamationThread;
  }
  return null;
};

export const rnReclamationUserLabel = (user?: RnReclamationUserRef | string | null): string => {
  if (!user || typeof user === 'string') return '';
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return fullName || user.userName || user.email || '';
};
